import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Trash, ArrowSquareOut, Calendar, User, ListChecks, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Semaforo, StatusBadge , EstadoBadge } from "../components/Semaforo";
import { COMMITMENT_TIPOS, COMMITMENT_ESTADOS } from "../lib/constants";
import { formatDate, formatDateTime } from "../lib/format";
import RichEditor, { RichView } from "../components/RichEditor";
import { LinksEditor, EvidenceUploader, TabLabel } from "../components/DetailHelpers";
import MoneyInput from "../components/MoneyInput";
import ReminderBar from "../components/ReminderBar";
import { useConfirm } from "../components/ConfirmDialog";


export default function CommitmentDetail() {
  const confirm = useConfirm();
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  // New progress update form
  const [updNew, setUpdNew] = useState({ fecha: "", descripcion: "", percent_avance: "", estado: "" });

  const load = async () => {
    const r = await api.get(`/commitments/${id}`);
    setData(r.data);
    const c = r.data.commitment;
    setForm({
      name: c.name || "",
      description: c.description || "",
      tipo: c.tipo || "operacional",
      origen: c.origen || "",
      related_entity_type: c.related_entity_type || "",
      related_entity_id: c.related_entity_id || "",
      project_id: c.project_id || "",
      rca_id: c.rca_id || "",
      responsable_interno: c.responsable_interno || "",
      responsable_externo: c.responsable_externo || "",
      responsable_email: c.responsable_email || "",
      fecha_inicio: c.fecha_inicio || "",
      fecha_compromiso: c.fecha_compromiso || "",
      fecha_cumplimiento: c.fecha_cumplimiento || "",
      estado: c.estado || "pendiente",
      beneficiarios_directos: c.beneficiarios_directos || 0,
      beneficiarios_indirectos: c.beneficiarios_indirectos || 0,
      inversion: c.inversion || 0,
      notas: c.notas || "",
      percent_avance: c.percent_avance || 0,
      assigned_to: c.assigned_to || null,
    });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  if (!data || !form) return <div className="p-8 text-slate-500">Cargando…</div>;
  const c = data.commitment;

  const save = async () => {
    try {
      setSaving(true);
      // Normalize empty strings → null for optional date/email/uuid fields so Pydantic accepts them
      const payload = { ...form };
      ["fecha_inicio", "fecha_compromiso", "fecha_cumplimiento",
       "responsable_email", "project_id", "rca_id",
       "related_entity_type", "related_entity_id"].forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      await api.put(`/commitments/${id}`, payload);
      toast.success("Compromiso actualizado");
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join("; ") : "Error al guardar";
      toast.error(msg);
      throw e;
    } finally { setSaving(false); }
  };

  const addUpdate = async () => {
    if (!updNew.descripcion || updNew.descripcion === "<p><br></p>") return toast.error("Describe el avance");
    try {
      await api.post(`/commitments/${id}/updates`, {
        fecha: updNew.fecha || null,
        descripcion: updNew.descripcion,
        percent_avance: updNew.percent_avance === "" ? null : Number(updNew.percent_avance),
        estado: updNew.estado || null,
      });
      toast.success("Avance registrado");
      setUpdNew({ fecha: "", descripcion: "", percent_avance: "", estado: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al registrar avance");
    }
  };

  const delUpdate = async (uid) => {
    if (!(await confirm({ title: "Eliminar avance", description: "¿Deseas eliminar este avance del compromiso?", danger: true }))) return;
    await api.delete(`/progress_updates/${uid}`);
    await load();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={c.name}
        description={
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge tone="info">{c.tipo}</StatusBadge>
            <EstadoBadge value={c.estado} />
            <Semaforo value={c.semaforo} />
            {c.fecha_compromiso && <span className="text-xs text-slate-500"><Calendar size={12} className="inline mr-1" />Compromiso: {formatDate(c.fecha_compromiso)}</span>}
            {(c.responsable_interno || c.responsable_email) && (
              <span className="text-xs text-slate-600 inline-flex items-center gap-1.5 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-0.5">
                <User size={12} />
                <span className="font-medium">{c.responsable_interno || "Sin nombre"}</span>
                {c.responsable_email && <span className="text-slate-500">· {c.responsable_email}</span>}
              </span>
            )}
          </div>
        }
      >
        <Button onClick={save} disabled={saving} className="bg-brand-green-700 hover:bg-brand-green-800 text-white" data-testid="header-save-btn">
          <FloppyDisk size={14} className="mr-1.5" /> {saving ? "Guardando…" : "Guardar cambios"}
        </Button>
        <Button variant="secondary" onClick={() => nav(-1)}>← Volver</Button>
      </PageHeader>

      <div className="px-6 space-y-6">
        {/* Reminder bar: start/end/days/email + force send */}
        <ReminderBar
          entityKind="commitments"
          entityId={id}
          form={form}
          setForm={setForm}
          startKey="fecha_inicio"
          endKey="fecha_compromiso"
          onSaveNeeded={save}
        />

        {/* Descripción del compromiso */}
        {c.description && (
          <Card className="rounded-md border-slate-200">
            <CardContent className="p-5">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Descripción del compromiso</div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap">{c.description}</div>
            </CardContent>
          </Card>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KPI label="Avance" value={`${c.percent_avance || 0}%`} accent="emerald" />
          <KPI label="Inversión" value={c.inversion ? `$${Number(c.inversion).toLocaleString("es-CL")}` : "—"} />
          <KPI label="Beneficiarios" value={(c.beneficiarios_directos || 0) + (c.beneficiarios_indirectos || 0)} />
          <KPI label="Origen" value={c.origen || "—"} />
          <KPI label="Tipo" value={c.tipo} />
        </div>

        {/* Quick bar: progress + estado */}
        <Card className="rounded-md">
          <CardContent className="p-4 space-y-3">
            <div>
              <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                <span>Avance del compromiso</span>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={form.percent_avance}
                    onChange={(e) => setForm({ ...form, percent_avance: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
                    className="h-7 w-20 text-right"
                    data-testid="percent-input"
                  />
                  <span className="font-semibold text-slate-900 w-6 text-right">%</span>
                </div>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                value={form.percent_avance}
                onChange={(e) => setForm({ ...form, percent_avance: Number(e.target.value) })}
                className="w-full accent-emerald-500"
                data-testid="quick-progress"
              />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Label className="text-xs">Estado</Label>
                <Select value={form.estado} onValueChange={(v) => setForm({ ...form, estado: v })}>
                  <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
                  <SelectContent>{COMMITMENT_ESTADOS.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button className="bg-slate-900" onClick={save} disabled={saving} data-testid="save-quickbar">
                <FloppyDisk size={14} className="mr-1" /> Guardar cambios
              </Button>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="seguimiento">
          <TabsList>
            <TabsTrigger value="seguimiento"><TabLabel label={`Seguimiento (${data.updates.length})`} hasData={data.updates.length > 0} /></TabsTrigger>
            <TabsTrigger value="detalle"><TabLabel label="Detalles" hasData /></TabsTrigger>
            <TabsTrigger value="vinculos"><TabLabel label="Vinculaciones" hasData={!!data.related || !!data.project} /></TabsTrigger>
            <TabsTrigger value="evidencias"><TabLabel label={`Evidencias (${data.documents.length})`} hasData={data.documents.length > 0} /></TabsTrigger>
          </TabsList>

          {/* SEGUIMIENTO */}
          <TabsContent value="seguimiento">
            <Card className="rounded-md mt-4">
              <CardHeader><CardTitle className="text-base">Registrar nuevo avance</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Fecha</Label>
                    <Input type="date" value={updNew.fecha} onChange={(e) => setUpdNew({ ...updNew, fecha: e.target.value })} />
                  </div>
                  <div>
                    <Label>% Avance</Label>
                    <Input type="number" min="0" max="100" value={updNew.percent_avance} onChange={(e) => setUpdNew({ ...updNew, percent_avance: e.target.value })} placeholder="opcional" />
                  </div>
                  <div>
                    <Label>Cambiar estado a</Label>
                    <Select value={updNew.estado || "__none__"} onValueChange={(v) => setUpdNew({ ...updNew, estado: v === "__none__" ? "" : v })}>
                      <SelectTrigger><SelectValue placeholder="No cambiar" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No cambiar</SelectItem>
                        {COMMITMENT_ESTADOS.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Descripción del avance</Label>
                  <RichEditor value={updNew.descripcion} onChange={(html) => setUpdNew({ ...updNew, descripcion: html })} placeholder="Detalla el avance, decisiones, próximos pasos…" />
                </div>
                <div className="text-right">
                  <Button onClick={addUpdate} className="bg-slate-900" data-testid="add-update-btn"><Plus size={14} className="mr-1" /> Registrar avance</Button>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-md mt-4">
              <CardHeader><CardTitle className="text-base">Bitácora de seguimiento</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {data.updates.length === 0 && <div className="text-sm text-slate-500 italic">Sin avances registrados todavía.</div>}
                {data.updates.map((u) => (
                  <div key={u.id} className="border-l-2 border-brand-green-400 pl-3 pb-2">
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-slate-500">
                        <Calendar size={12} className="inline mr-1" />{formatDate(u.fecha)}
                        {u.author_name && <span className="ml-2"><User size={12} className="inline mr-0.5" />{u.author_name}</span>}
                        <span className="ml-3 text-[10px] text-slate-400">{formatDateTime(u.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {u.percent_avance !== null && u.percent_avance !== undefined && <StatusBadge tone="success">{u.percent_avance}%</StatusBadge>}
                        {u.estado && <EstadoBadge value={u.estado} />}
                        <Button size="sm" variant="ghost" onClick={() => delUpdate(u.id)}><Trash size={12} className="text-red-600" /></Button>
                      </div>
                    </div>
                    <RichView html={u.descripcion} />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* DETALLE EDITABLE */}
          <TabsContent value="detalle">
            <Card className="rounded-md mt-4">
              <CardHeader><CardTitle className="text-base">Información editable</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="col-span-2"><Label>Descripción</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div>
                  <Label>Tipo</Label>
                  <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{COMMITMENT_TIPOS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Origen</Label><Input value={form.origen} onChange={(e) => setForm({ ...form, origen: e.target.value })} placeholder="Ej: convenio_comunidad, mesa_dialogo, rsc" /></div>
                <div><Label>Responsable interno</Label><Input value={form.responsable_interno} onChange={(e) => setForm({ ...form, responsable_interno: e.target.value })} /></div>
                <div><Label>Responsable externo</Label><Input value={form.responsable_externo} onChange={(e) => setForm({ ...form, responsable_externo: e.target.value })} /></div>
                <div className="col-span-2"><Label>Email(s) responsable — separa con coma para varios</Label><Input type="text" value={form.responsable_email} onChange={(e) => setForm({ ...form, responsable_email: e.target.value })} placeholder="nombre@empresa.cl, otro@empresa.cl" /></div>
                <div><Label>Fecha inicio</Label><Input type="date" value={form.fecha_inicio || ""} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value || null })} /></div>
                <div><Label>Fecha compromiso</Label><Input type="date" value={form.fecha_compromiso || ""} onChange={(e) => setForm({ ...form, fecha_compromiso: e.target.value || null })} /></div>
                <div><Label>Fecha cumplimiento</Label><Input type="date" value={form.fecha_cumplimiento || ""} onChange={(e) => setForm({ ...form, fecha_cumplimiento: e.target.value || null })} /></div>
                <div><Label>Inversión (CLP)</Label><MoneyInput value={form.inversion} onChange={(n) => setForm({ ...form, inversion: n })} /></div>
                <div><Label>% Avance</Label><Input type="number" min="0" max="100" value={form.percent_avance} onChange={(e) => setForm({ ...form, percent_avance: Number(e.target.value) })} /></div>
                <div><Label>Beneficiarios directos</Label><Input type="number" value={form.beneficiarios_directos} onChange={(e) => setForm({ ...form, beneficiarios_directos: Number(e.target.value) })} /></div>
                <div><Label>Beneficiarios indirectos</Label><Input type="number" value={form.beneficiarios_indirectos} onChange={(e) => setForm({ ...form, beneficiarios_indirectos: Number(e.target.value) })} /></div>
                <div className="col-span-2"><Label>Notas</Label><Textarea rows={3} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} /></div>
                <div className="col-span-2 text-right">
                  <Button onClick={save} disabled={saving} className="bg-slate-900"><FloppyDisk size={14} className="mr-1" /> Guardar</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* VINCULACIONES */}
          <TabsContent value="vinculos">
            <LinksEditor
              related={data.related}
              project={data.project}
              showProject
              onChange={async (patch) => {
                const updated = { ...form, ...patch };
                setForm(updated);
                await api.put(`/commitments/${id}`, updated);
                await load();
              }}
            />
          </TabsContent>

          {/* EVIDENCIAS */}
          <TabsContent value="evidencias">
            <EvidenceUploader
              entityType="commitment"
              entityId={id}
              documents={data.documents}
              onReload={load}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}


function KPI({ label, value, accent }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-lg font-semibold mt-1 ${accent === "emerald" ? "text-brand-green-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}
